import { create } from 'zustand';
import { walletService } from '../services/walletService';
import { transactionService } from '../services/transactionService';
import {
  UnifiedTransaction,
  UnifiedTransactionType,
  TransactionFilterType,
  WalletTransaction,
  Transaction,
} from '../types';
import { handleApiError, formatErrorForDisplay, logError } from '../lib/errorHandler';

interface UnifiedTransactionState {
  // State
  unifiedTransactions: UnifiedTransaction[];
  activeTransaction: UnifiedTransaction | null;
  filter: TransactionFilterType;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Actions
  fetchUnifiedTransactions: (page?: number) => Promise<void>;
  loadMoreUnifiedTransactions: () => Promise<void>;
  refreshUnifiedTransactions: () => Promise<void>;
  setFilter: (filter: TransactionFilterType) => void;
  clearError: () => void;
}

const DEFAULT_PAGE_LIMIT = 20;

/**
 * Transform wallet transaction to unified format
 */
const transformWalletTransaction = (wt: WalletTransaction): UnifiedTransaction => {
  return {
    id: `wallet-${wt.id}`,
    type: wt.type === 'credit' ? 'wallet_credit' : 'wallet_debit',
    date: new Date(wt.createdAt),
    description: wt.description,
    amount: wt.amount,
    status: wt.status,
    balanceAfter: wt.balanceAfter,
    linkToCharging: wt.chargingTransactionId,
    canDownloadInvoice: (wt.source == null || wt.source === 'recharge') && wt.type === 'credit' && wt.status === 'completed' && wt.amount > 0,
    walletTransaction: wt,
  };
};

/**
 * Transform charging transaction to unified format
 */
const transformChargingTransaction = (ct: Transaction): UnifiedTransaction => {
  return {
    id: `charging-${ct.id}`,
    type: 'charging_session',
    date: new Date(ct.stoppedAt || ct.createdAt),
    description: `Charging at ${ct.chargePoint?.location?.name || ct.chargePoint?.name || 'Unknown Location'}`,
    amount: ct.totalCost ?? 0,
    energy: ct.totalKwh ?? 0,
    status: ct.isActive ? 'active' : 'completed',
    canDownloadInvoice: false,
    chargingTransaction: ct,
  };
};

/**
 * Unified Transaction Store
 * Manages combined wallet and charging transaction history with filtering and pagination
 */
export const useUnifiedTransactionStore = create<UnifiedTransactionState>((set, get) => ({
  // Initial state
  unifiedTransactions: [],
  activeTransaction: null,
  filter: 'charging',
  currentPage: 0,
  totalPages: 0,
  hasMore: true,
  isLoading: false,
  isLoadingMore: false,
  error: null,

  /**
   * Fetch unified transactions (wallet + charging) with pagination and filtering
   */
  fetchUnifiedTransactions: async (page: number = 1) => {
    const isFirstPage = page === 1;
    const { filter } = get();

    // Set loading state
    set({
      isLoading: isFirstPage,
      isLoadingMore: !isFirstPage,
      error: null,
    });

    try {
      // Optimized: Only call the API needed for the current filter
      let merged: UnifiedTransaction[] = [];
      let hasMoreData = false;
      let maxTotalPages = 0;

      if (filter === 'credits') {
        // Only fetch wallet credit transactions
        const walletResponse = await walletService.getHistory({
          page,
          limit: DEFAULT_PAGE_LIMIT,
          type: 'credit',
        });
        merged = walletResponse.transactions.map(transformWalletTransaction);
        hasMoreData = walletResponse.pagination.page < walletResponse.pagination.totalPages;
        maxTotalPages = walletResponse.pagination.totalPages;
      } else if (filter === 'debits') {
        // Only fetch wallet debit transactions
        const walletResponse = await walletService.getHistory({
          page,
          limit: DEFAULT_PAGE_LIMIT,
          type: 'debit',
        });
        merged = walletResponse.transactions.map(transformWalletTransaction);
        hasMoreData = walletResponse.pagination.page < walletResponse.pagination.totalPages;
        maxTotalPages = walletResponse.pagination.totalPages;
      } else if (filter === 'charging') {
        // Only fetch charging transactions (including active ones)
        const chargingResponse = await transactionService.getMyTransactions({
          page,
          limit: DEFAULT_PAGE_LIMIT,
        });
        // Include ALL charging transactions (active and completed)
        merged = chargingResponse.data.map(transformChargingTransaction);
        hasMoreData = chargingResponse.hasNextPage;
        maxTotalPages = chargingResponse.totalPages;
      }

      set((state) => ({
        unifiedTransactions: isFirstPage ? merged : [...state.unifiedTransactions, ...merged],
        activeTransaction: null, // Active sessions now appear in the list
        currentPage: page,
        totalPages: maxTotalPages,
        hasMore: hasMoreData,
        isLoading: false,
        isLoadingMore: false,
      }));
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'UnifiedTransactionStore.fetchUnifiedTransactions');
      set({
        error: formatErrorForDisplay(appError),
        isLoading: false,
        isLoadingMore: false,
      });
      throw error;
    }
  },

  /**
   * Load more unified transactions (for infinite scroll)
   */
  loadMoreUnifiedTransactions: async () => {
    const { hasMore, isLoadingMore, isLoading, currentPage } = get();

    // Prevent duplicate requests
    if (!hasMore || isLoadingMore || isLoading) {
      console.log('[UnifiedTransactionStore] Skipping load more', { hasMore, isLoadingMore, isLoading });
      return;
    }

    console.log('[UnifiedTransactionStore] Loading more transactions...', { currentPage });

    // Load next page
    await get().fetchUnifiedTransactions(currentPage + 1);
  },

  /**
   * Refresh unified transactions (reset to page 1)
   */
  refreshUnifiedTransactions: async () => {
    console.log('[UnifiedTransactionStore] Refreshing transactions');

    // Reset state and reload from page 1
    set({
      currentPage: 0,
      hasMore: true,
      unifiedTransactions: [],
    });

    await get().fetchUnifiedTransactions(1);
  },

  /**
   * Set filter and refetch transactions
   */
  setFilter: async (filter: TransactionFilterType) => {
    console.log('[UnifiedTransactionStore] Setting filter', filter);

    // Update filter and reset pagination
    set({
      filter,
      currentPage: 0,
      hasMore: true,
      unifiedTransactions: [],
    });

    // Refetch with new filter
    await get().fetchUnifiedTransactions(1);
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },
}));
